import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import { ItemGridComponent } from "../old/item-grid/item-grid.component";
import { Category, IBrandCard, IPlan } from '../types';
import { HttpClient } from '@angular/common/http';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { backend } from '../../environments/environment';
import { catchError, combineLatest, debounceTime, map, of, scan, startWith, switchMap, tap } from 'rxjs';
import { LocationService } from '../location.service';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { BrandCardComponent } from "../old/brand-card/brand-card.component";
import { MatIconModule } from '@angular/material/icon';
import { RouterLink } from '@angular/router';
import { BucketPipe } from '../bucket.pipe';
import { CacheService } from '../auth.service';
import { MatButtonModule } from '@angular/material/button';
import { PaintDirective } from '../paint.directive';
import { MetaService } from '../meta.service';

@Component({
  selector: 'cloudigo-search',
  standalone: true,
  imports: [ItemGridComponent, BrandCardComponent, ReactiveFormsModule, MatIconModule, RouterLink, BucketPipe, MatButtonModule, PaintDirective],
  templateUrl: './search.component.html',
  styleUrl: './search.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export default class SearchComponent {
  http = inject(HttpClient);
  router = inject(LocationService);
  cache = inject(CacheService);
  meta = inject(MetaService);

  query = new FormControl<string>('', { nonNullable: true });

  search = input<string>('', { alias: 's'});
  page = signal<number>(0);

  loading = signal<boolean>(false);

  final = signal<boolean>(false);

  categories = toSignal(this.http.get<Category[]>(backend + 'v2/categories/list').pipe(takeUntilDestroyed(), catchError(() => []), tap(categories => {
    categories.forEach(category => {
      this.cache.registerIconFromSVG(category.slug, category.icon_svg);
    })
  })));

  brands = toSignal(combineLatest([toObservable(this.search).pipe(tap(() => this.loading.set(true)), tap(value => { this.page.set(0); this.final.set(false); this.query.setValue(value, { emitEvent: false }); })), toObservable(this.page)]).pipe(
    tap(() => this.loading.set(true)),
    debounceTime(100),
    switchMap(([query,page]) => query ? this.http.get<IBrandCard[] | { brands: IBrandCard[], plans: IPlan[] }>(backend + 'v3/brands/featured', {
      params: {
        ...query ? {
          sort: 'search',
          query: query.trim(),
        } : {
          sort: 'popularity'
        },
        page,
        limit: 20,
        tags: 'search,with_tags'
      }
    }).pipe(catchError(e => of([] as IBrandCard[]))): of([])),
    map(value => {
      const brands = [];
      if('brands' in value){
        brands.push(...value.brands);
        this.cache.registerPlans(value.plans);
      } else {
        brands.push(...value);
      }
      brands.forEach((b,i) => b._id += i);
      return brands;
    }),
    tap(brands => this.final.set(brands.length < 20)),
    scan((total, added) => (this.page() == 0 ? added : [...total, ...added]), [] as IBrandCard[]),
    startWith([] as IBrandCard[]),
    tap(() => this.loading.set(false))
  ), { initialValue: [] });

  constructor(){
    this.meta.setTags('cloudigo Malta - Search all offers', 'Discover offers from over 10 different categories including fashion, dining, shopping, kids, beauty, sports & so much more with up to 50% OFF!');
    this.query.valueChanges.pipe(takeUntilDestroyed(), debounceTime(500)).subscribe(query => {
      this.router.navigate(['/search'], {
        queryParamsHandling: 'merge',
        queryParams: { s: query.trim() }
      });
    })
  }

  loadMore(){
    this.page.update(page => page+1);
  }
}
